import { input } from '@b2b/types/core';
import angular, { INgModelController, IScope, ITimeoutService } from 'angular';
import _ from 'lodash';
import {
  FieldsetModelMergerField,
  ModelMergerField,
  isFieldsetModelMergerField,
  isSingleModelMergerField,
} from './types';

class ModelMergerController<TModel extends object> {
  static $inject = ['$scope', '$timeout'];

  ngModel!: TModel;
  ngModelCtrl!: INgModelController;
  state = input.required<'editing' | 'viewing'>('editing');
  fields = input.required<ModelMergerField<TModel>[]>([]);
  candidates = input.required<TModel[]>([]);
  modelMappers = input.required({
    getId: (candidate: TModel): string => {
      throw new Error('Not implemented yet');
    },
    getColumnHeader: (candidate: TModel): string => {
      throw new Error('Not implemented yet');
    },
    getColumnSubHeader: (candidate: TModel): string => {
      throw new Error('Not implemented yet');
    },
  });

  get filteredCandidates() {
    if (this.state === 'editing') {
      return this.candidates;
    }

    return [this.ngModel];
  }

  constructor(private $scope: IScope, private $timeout: ITimeoutService) {}

  $onInit() {
    this.$timeout(() => {
      this.preselectDefaultBaseModel();
    }, 100);
  }

  ngModelChange(candidate: TModel, field: ModelMergerField<TModel>) {
    field.onChange?.({ candidate, selectAll: () => this.selectAllFrom(candidate) });
    this.ngModelCtrl.$setViewValue(this.ngModel);
  }

  isEmpty(value: unknown) {
    return value == null || value === '';
  }

  getValue(candidate: TModel, path: string) {
    return _.get(candidate, path);
  }

  emptyOrValue(candidate: TModel, path: string) {
    const value = this.getValue(candidate, path);

    return this.isEmpty(value) ? '[empty]' : value;
  }

  selectAllFrom(candidate: TModel) {
    for (const field of this.fields) {
      if (isSingleModelMergerField(field)) {
        this.ngModel[field.key] = candidate[field.key];
        field.onChange?.({ candidate });
      } else if (isFieldsetModelMergerField(field)) {
        const fieldset = field as FieldsetModelMergerField<TModel>;
        this.ngModel[fieldset.key] = _.cloneDeep(candidate[fieldset.key]);
        fieldset.onChange?.({ candidate });
      }
    }

    this.ngModelCtrl.$setViewValue(this.ngModel);
    this.simulateClicks(candidate);
  }

  private preselectDefaultBaseModel() {
    if (this.candidates.length === 0) {
      return;
    }

    this.selectAllFrom(this.candidates[0]);
  }

  /**
   * We need to programatically click on the radio buttons to make sure fieldsets are correctly
   * selected because we're grouping multiple properties under the same radio button.
   */
  private simulateClicks(candidate: TModel) {
    this.$timeout(() => {
      const candidateId = this.modelMappers.getId(candidate);
      document.querySelectorAll<HTMLInputElement>(`[data-candidate-id="${candidateId}"]`).forEach((radio) => {
        radio.checked = true;
        radio.click();
      });
    }, 0);
  }
}

export const ModelMergerComponentModule = angular
  .module('acme.common.component.model-merger', [])
  .component('acmeModelMerger', {
    templateUrl: 'common/components/model-merger/model-merger.component.tpl.html',
    controller: ModelMergerController,
    require: {
      ngModelCtrl: 'ngModel',
    },
    bindings: {
      ngModel: '<',
      state: '<',
      fields: '<',
      candidates: '<',
      modelMappers: '<',
    },
  }).name;
